import { useMemo, useEffect, useState } from 'react';
import { useSpecificationCategories } from '@/hooks/useSpecificationCategories';
import { useSpecificationFieldDefinitions } from '@/hooks/useSpecificationFieldDefinitions';
import { Skeleton } from '@/components/ui/skeleton';
import { SpecificationDocumentCard } from './SpecificationDocumentCard';
import { supabase } from '@/integrations/supabase/client';

interface SpecificationDataRendererProps {
  content: any;
  specification?: any;
}

export const SpecificationDataRenderer = ({ content, specification }: SpecificationDataRendererProps) => {
  // Check if this is a document-based specification
  const isDocumentBased = content?.input_method === 'upload';
  // Check if this is a master specification with structured data
  const hasMasterData = !isDocumentBased && content?.specification_data && content?.product_category_id;

  // Fetch categories and fields if it's a master specification
  // Include inactive ones to handle historical data
  const { categories, loading: categoriesLoading } = useSpecificationCategories(true);
  const { fields, loading: fieldsLoading } = useSpecificationFieldDefinitions({ includeInactive: true });

  // Build lookup maps for categories and fields
  const { categoryMap, fieldMap } = useMemo(() => {
    const catMap = new Map<string, any>();
    const fldMap = new Map<string, any>();

    if (categories && categories.length > 0) {
      categories.forEach(cat => {
        catMap.set(cat.id, cat);
      });
    }

    if (fields && fields.length > 0) {
      fields.forEach(field => {
        fldMap.set(field.id, field);
      });
    }

    return { categoryMap: catMap, fieldMap: fldMap };
  }, [categories, fields]);

  // Helper function to format values based on field type
  const formatValue = (value: any, fieldDef?: any): string => {
    if (value === null || value === undefined) return '-';
    
    // Boolean formatting
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    
    // Array formatting (for multiselect)
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    
    // Add unit of measure if available
    if (fieldDef?.unit_of_measure && value) {
      return `${value} ${fieldDef.unit_of_measure}`;
    }
    
    return String(value);
  };

  // Helper to check if value is non-empty
  const isNonEmpty = (v: any): boolean => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.values(v).some(isNonEmpty);
    return true;
  };

  // Helper to format simple labels
  const formatLabel = (k: string) =>
    k
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Attempt to resolve a document ID from the source template when missing
  const [fallbackDocId, setFallbackDocId] = useState<string | null>(null);
  useEffect(() => {
    const fetchTemplateDoc = async () => {
      if (!isDocumentBased) return;
      if (specification?.specification_document_id) return;
      if (!specification?.source_template_id) return;
      try {
        const { data, error } = await supabase
          .from('specifications')
          .select('specification_document_id')
          .eq('id', specification.source_template_id)
          .maybeSingle();
        if (!error && data?.specification_document_id) {
          setFallbackDocId(data.specification_document_id);
        }
      } catch (e) {
        console.warn('Could not resolve template document id', e);
      }
    };
    fetchTemplateDoc();
  }, [isDocumentBased, specification?.specification_document_id, specification?.source_template_id]);

  const resolvedDocumentId = specification?.specification_document_id || fallbackDocId;

  // Render document-based specification
  if (isDocumentBased && resolvedDocumentId) {
    return (
      <div className="space-y-4">
        <SpecificationDocumentCard
          documentId={resolvedDocumentId}
          specificationName={specification?.specification_name}
          subcategoryName={content.subcategory_name}
          productCategoryName={content.product_category_name}
          showDownloadButton={true}
          showMetadata={true}
        />
        {content.additional_notes && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm mb-2">Additional Notes</h4>
            <p className="text-sm whitespace-pre-wrap">{content.additional_notes}</p>
          </div>
        )}
      </div>
    );
  }

  // Render master specification data
  if (hasMasterData) {
    if (categoriesLoading || fieldsLoading) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }

    const specData = content.specification_data as Record<string, Record<string, any>>;
    const categoryIds = Object.keys(specData);

    // Filter out empty categories
    const nonEmptyCategories = categoryIds.filter(catId => {
      const fieldsData = specData[catId];
      return Object.values(fieldsData).some(isNonEmpty);
    });

    return (
      <div className="space-y-4">
        {nonEmptyCategories.map((categoryId) => {
          const category = categoryMap.get(categoryId);
          const categoryName = category?.name || 'Unknown Category';
          const fieldsData = specData[categoryId];
          
          // Filter out empty fields
          const nonEmptyFields = Object.entries(fieldsData).filter(([_, value]) => isNonEmpty(value));
          
          if (nonEmptyFields.length === 0) return null;

          return (
            <div key={categoryId} className="border-b last:border-b-0 pb-4 last:pb-0">
              <h4 className="font-semibold text-sm mb-3">{categoryName}</h4>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {nonEmptyFields.map(([fieldId, value]) => {
                  const field = fieldMap.get(fieldId);
                  const fieldLabel = field?.field_label || formatLabel(fieldId);
                  
                  return (
                    <div key={fieldId}>
                      <dt className="text-muted-foreground text-xs">{fieldLabel}</dt>
                      <dd className="text-sm font-medium">{formatValue(value, field)}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          );
        })}

        {/* Render additional notes if present */}
        {content.additional_notes && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm mb-2">Additional Notes</h4>
            <p className="text-sm whitespace-pre-wrap">{content.additional_notes}</p>
          </div>
        )}

        {nonEmptyCategories.length === 0 && !content.additional_notes && (
          <span className="text-muted-foreground">No specification details provided.</span>
        )}
      </div>
    );
  }

  // Fallback: Render simple specification data (non-master specs)
  const entries = Object.entries(content || {}).filter(([_, v]) => isNonEmpty(v));

  return (
    <div className="space-y-2">
      {entries.length > 0 ? (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entries.map(([k, v]) => (
            <div key={k}>
              <dt className="text-muted-foreground">{formatLabel(k)}</dt>
              <dd>
                {Array.isArray(v) ? (
                  <ul className="list-disc list-inside">
                    {v.map((item: any, idx: number) => (
                      <li key={idx}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
                    ))}
                  </ul>
                ) : typeof v === 'object' ? (
                  <div className="space-y-1">
                    {Object.entries(v)
                      .filter(([_, vv]) => isNonEmpty(vv))
                      .map(([kk, vv]) => (
                        <div key={kk}>
                          <span className="text-muted-foreground">{formatLabel(kk)}:</span> {String(vv)}
                        </div>
                      ))}
                  </div>
                ) : (
                  String(v)
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <span className="text-muted-foreground">No details provided.</span>
      )}
    </div>
  );
};
